import { useCallback } from 'react';
import { Product, ProductVariant } from '../api/models';

// Tipos para los eventos de Zaraz
export interface ZarazProduct {
  product_id: string;
  sku?: string;
  category?: string;
  name: string;
  brand?: string;
  variant?: string;
  price: number;
  quantity?: number;
  coupon?: string;
  position?: number;
}

export interface ZarazCheckout {
  checkout_id?: string;
  order_id?: string;
  affiliation?: string;
  total: number;
  revenue: number;
  shipping?: number;
  tax?: number;
  discount?: number;
  coupon?: string;
  currency: string;
  value: number;
}

export interface ZarazPromotion {
  creative?: string;
  query?: string;
}

export interface ZarazPayment {
  payment_type?: string;
}

export interface ZarazCheckoutStep {
  step: number;
}

export interface ZarazUser {
  user_id?: string;
  email?: string;
  name?: string;
  role?: string;
}

// Hook personalizado para Zaraz tracking
export const useZarazTracking = () => {
  // Función base para enviar eventos a Zaraz
  const trackEvent = useCallback((eventName: string, parameters: Record<string, any> = {}) => {
    // Verificar si Zaraz está disponible
    if (typeof window !== 'undefined' && (window as any).zaraz) {
      try {
        (window as any).zaraz.track(eventName, parameters);
        console.log(`🔍 [Zaraz] Evento enviado: ${eventName}`, parameters);
      } catch (error) {
        console.error(`❌ [Zaraz] Error enviando evento ${eventName}:`, error);
      }
    } else {
      console.warn(`⚠️ [Zaraz] Zaraz no está disponible para el evento: ${eventName}`);
    }
  }, []);

  // Convertir producto a formato Zaraz
  const productToZaraz = useCallback((product: Product, variant?: ProductVariant | null, quantity: number = 1, position?: number): ZarazProduct => {
    return {
      product_id: product.id,
      sku: variant?.sku || product.sku,
      category: product.category?.name || product.category?.slug,
      name: product.name,
      brand: product.brand,
      variant: variant?.name,
      price: variant?.price || product.price || 0,
      quantity,
      position
    };
  }, []);

  // Eventos de productos
  const trackProductListViewed = useCallback((products: Product[], listName?: string) => {
    const zarazProducts = products.map((product, index) => 
      productToZaraz(product, null, 1, index + 1)
    );
    
    trackEvent('Product List Viewed', {
      products: zarazProducts,
      list_name: listName
    });
  }, [trackEvent, productToZaraz]);

  const trackProductsSearched = useCallback((query: string, products: Product[]) => {
    const zarazProducts = products.map((product, index) => 
      productToZaraz(product, null, 1, index + 1)
    );
    
    trackEvent('Products Searched', {
      query,
      products: zarazProducts
    });
  }, [trackEvent, productToZaraz]);

  const trackProductClicked = useCallback((product: Product, variant?: ProductVariant | null, position?: number) => {
    trackEvent('Product Clicked', productToZaraz(product, variant, 1, position));
  }, [trackEvent, productToZaraz]);

  const trackProductViewed = useCallback((product: Product, variant?: ProductVariant | null) => {
    trackEvent('Product Viewed', productToZaraz(product, variant));
  }, [trackEvent, productToZaraz]);

  const trackProductAdded = useCallback((product: Product, variant?: ProductVariant | null, quantity: number = 1) => {
    trackEvent('Product Added', productToZaraz(product, variant, quantity));
  }, [trackEvent, productToZaraz]);

  const trackProductAddedToWishlist = useCallback((product: Product, variant?: ProductVariant | null) => {
    trackEvent('Product Added to Wishlist', productToZaraz(product, variant));
  }, [trackEvent, productToZaraz]);

  const trackProductRemoved = useCallback((product: Product, variant?: ProductVariant | null, quantity: number = 1) => {
    trackEvent('Product Removed', productToZaraz(product, variant, quantity));
  }, [trackEvent, productToZaraz]);

  // Eventos de carrito
  const trackCartViewed = useCallback((products: Array<{product: Product, variant?: ProductVariant | null, quantity: number}>) => {
    const zarazProducts = products.map(item => 
      productToZaraz(item.product, item.variant, item.quantity)
    );
    
    trackEvent('Cart Viewed', {
      products: zarazProducts
    });
  }, [trackEvent, productToZaraz]);

  // Eventos de checkout
  const trackCheckoutStarted = useCallback((checkoutData: Partial<ZarazCheckout>) => {
    trackEvent('Checkout Started', {
      ...checkoutData,
      currency: checkoutData.currency || 'EUR'
    });
  }, [trackEvent]);

  const trackCheckoutStepViewed = useCallback((step: number, checkoutData?: Partial<ZarazCheckout>) => {
    trackEvent('Checkout Step Viewed', {
      step,
      ...checkoutData,
      currency: checkoutData?.currency || 'EUR'
    });
  }, [trackEvent]);

  const trackCheckoutStepCompleted = useCallback((step: number, checkoutData?: Partial<ZarazCheckout>) => {
    trackEvent('Checkout Step Completed', {
      step,
      ...checkoutData,
      currency: checkoutData?.currency || 'EUR'
    });
  }, [trackEvent]);

  const trackPaymentInfoEntered = useCallback((paymentData: ZarazPayment, checkoutData?: Partial<ZarazCheckout>) => {
    trackEvent('Payment Info Entered', {
      ...paymentData,
      ...checkoutData,
      currency: checkoutData?.currency || 'EUR'
    });
  }, [trackEvent]);

  const trackShippingInfoEntered = useCallback((checkoutData?: Partial<ZarazCheckout>) => {
    trackEvent('Shipping Info Entered', {
      ...checkoutData,
      currency: checkoutData?.currency || 'EUR'
    });
  }, [trackEvent]);

  // Eventos de órdenes
  const trackOrderCompleted = useCallback((orderData: ZarazCheckout) => {
    trackEvent('Order Completed', {
      ...orderData,
      currency: orderData.currency || 'EUR'
    });
  }, [trackEvent]);

  const trackOrderUpdated = useCallback((orderId: string, orderData: Partial<ZarazCheckout>) => {
    trackEvent('Order Updated', {
      order_id: orderId,
      ...orderData,
      currency: orderData.currency || 'EUR'
    });
  }, [trackEvent]);

  const trackOrderRefunded = useCallback((orderId: string, refundData: Partial<ZarazCheckout>) => {
    trackEvent('Order Refunded', {
      order_id: orderId,
      ...refundData,
      currency: refundData.currency || 'EUR'
    });
  }, [trackEvent]);

  const trackOrderCancelled = useCallback((orderId: string, orderData: Partial<ZarazCheckout>) => {
    trackEvent('Order Cancelled', {
      order_id: orderId,
      ...orderData,
      currency: orderData.currency || 'EUR'
    });
  }, [trackEvent]);

  // Eventos de promociones
  const trackClickedPromotion = useCallback((promotionData: ZarazPromotion) => {
    trackEvent('Clicked Promotion', promotionData);
  }, [trackEvent]);

  const trackViewedPromotion = useCallback((promotionData: ZarazPromotion) => {
    trackEvent('Viewed Promotion', promotionData);
  }, [trackEvent]);

  // Eventos de autenticación
  const trackSignedUp = useCallback((userData: ZarazUser) => {
    trackEvent('Signed Up', {
      user_id: userData.user_id,
      email: userData.email,
      name: userData.name,
      role: userData.role
    });
  }, [trackEvent]);

  const trackSignedIn = useCallback((userData: ZarazUser) => {
    trackEvent('Signed In', {
      user_id: userData.user_id,
      email: userData.email,
      name: userData.name,
      role: userData.role
    });
  }, [trackEvent]);

  const trackSignedOut = useCallback((userData?: ZarazUser) => {
    trackEvent('Signed Out', {
      user_id: userData?.user_id,
      email: userData?.email
    });
  }, [trackEvent]);

  return {
    // Función base
    trackEvent,
    
    // Eventos de productos
    trackProductListViewed,
    trackProductsSearched,
    trackProductClicked,
    trackProductViewed,
    trackProductAdded,
    trackProductAddedToWishlist,
    trackProductRemoved,
    
    // Eventos de carrito
    trackCartViewed,
    
    // Eventos de checkout
    trackCheckoutStarted,
    trackCheckoutStepViewed,
    trackCheckoutStepCompleted,
    trackPaymentInfoEntered,
    trackShippingInfoEntered,
    
    // Eventos de órdenes
    trackOrderCompleted,
    trackOrderUpdated,
    trackOrderRefunded,
    trackOrderCancelled,
    
    // Eventos de promociones
    trackClickedPromotion,
    trackViewedPromotion,

    // Eventos de autenticación
    trackSignedUp,
    trackSignedIn,
    trackSignedOut
  };
};

export default useZarazTracking;



